import React from 'react';
import { Users, Clock, Shield, Target, Heart, Car, Plane, MapPin, Building } from 'lucide-react';

const AboutPage = () => {
  const stats = [
    { icon: <Users className="w-8 h-8" />, number: "5000+", label: "Happy Customers" },
    { icon: <Car className="w-8 h-8" />, number: "10000+", label: "Vehicles Fleet" },
    { icon: <Clock className="w-8 h-8" />, number: "8+", label: "Years Experience" },
    { icon: <Shield className="w-8 h-8" />, number: "24/7", label: "Support Available" }
  ];

  const handleClick = () => {
    alert('Navigate to vehicles page');
  };

  const values = [
    {
      icon: <Target className="w-12 h-12" />,
      title: "Excellence",
      description: "We strive for excellence in every aspect of our service, from vehicle quality to customer care."
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Trust",
      description: "Built on transparency and integrity, we ensure honest dealings with no hidden costs."
    },
    {
      icon: <Heart className="w-12 h-12" />,
      title: "Customer First",
      description: "Your satisfaction drives us. We go above and beyond to exceed your expectations."
    }
  ];

  const services = [
    {
      icon: <Plane className="w-10 h-10" />,
      title: "Airport Transfers",
      description: "Start or end your journey stress-free with our prompt airport transfer services. We provide on-time pickups and drop-offs at all major airports.",
      features: ["Available 24×7", "Meet & Greet assistance", "Fixed and transparent pricing"]
    },
    {
      icon: <Car className="w-10 h-10" />,
      title: "City Rides",
      description: "Explore your city comfortably with My Journey Cabs. From business meetings to shopping trips, family outings, or medical visits.",
      features: ["Sedan, SUV, or Luxury Car", "Flexible booking", "Comfortable rides"]
    },
    {
      icon: <MapPin className="w-10 h-10" />,
      title: "Outstation Trips",
      description: "Discover new destinations with our reliable outstation cab service. Perfect for weekend getaways or long vacations.",
      features: ["Unlimited kilometers", "Flexible packages", "Doorstep service"]
    },
    {
      icon: <Building className="w-10 h-10" />,
      title: "Corporate Travel",
      description: "Professionalism meets comfort with our corporate cab services. Ideal for office commutes, business meetings, and VIP transfers.",
      features: ["Punctual chauffeurs", "Premium cars", "Customized billing"]
    },
    {
      icon: <Heart className="w-10 h-10" />,
      title: "Wedding Services",
      description: "Make your special day grand and hassle-free with our wedding transportation services.",
      features: ["Luxury cars for bride & groom", "Guest transfers", "Beautifully maintained vehicles"]
    },
    {
      icon: <Clock className="w-10 h-10" />,
      title: "Hourly Rentals",
      description: "Need a cab for a few hours? Our hourly rental service gives you full flexibility for city errands or personal use.",
      features: ["Pay only for hours used", "Dedicated cab & driver", "Full flexibility"]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-yellow-50 via-yellow-200 to-orange-50 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="inline-block mb-4">
              <span className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wider shadow-md">
                About Us
              </span>
            </div>
            <h1 className="text-6xl font-bold mb-6 text-gray-900">
              My Journey Cabs
              <span className="block text-yellow-600 mt-2">Since 2018</span>
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Your trusted travel partner across India! We provide reliable, comfortable, and affordable cab services for every occasion.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-xl p-6 text-center transform hover:scale-105 transition-transform duration-300 border-2 border-yellow-100">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl mb-4 text-white shadow-lg">
                {stat.icon}
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2">{stat.number}</div>
              <div className="text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Introduction Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Welcome to My Journey Cabs
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            Whether you're planning an airport pickup, city tour, outstation adventure, or a corporate trip, 
            we have the perfect ride ready for you.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            With a wide fleet of Sedans, SUVs, Tempo Travellers, Luxury Cars, and Buses, we ensure your journey 
            is smooth, safe, and enjoyable. Our experienced chauffeurs, clean vehicles, and 24×7 customer support 
            make us one of the most preferred cab services in the country.
          </p>
        </div>
      </div>

      {/* Services Section */}
      <div className="bg-gradient-to-b from-white to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
         
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 group border-2 border-blue-100 hover:border-yellow-400">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl mb-6 text-white group-hover:scale-110 transition-transform shadow-lg">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-2 text-gray-700">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full shadow-sm"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Story Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-block mb-4">
              <span className="text-yellow-600 font-bold uppercase tracking-wider text-sm">
                Our Journey
              </span>
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              Building Trust, One Mile at a Time
            </h2>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                Founded in 2023, we began with a simple vision: to revolutionize the car rental 
                experience by putting customers first. What started as a small operation with 
                just a handful of vehicles has grown into a trusted network serving thousands.
              </p>
              <p>
                Our fleet of meticulously maintained vehicles spans every category, 
                from fuel-efficient economy cars to luxurious premium models. Each vehicle 
                undergoes rigorous quality checks to ensure your safety and comfort.
              </p>
              <p>
                Today, we're proud to be recognized as an industry leader, but we haven't 
                forgotten our roots. Every customer interaction, every rental, every mile 
                driven reflects our commitment to the values that built this company.
              </p>
            </div>
            <div className="mt-8 flex gap-4">
              <div className="bg-yellow-400 h-1 w-20 rounded"></div>
              <div className="bg-gray-300 h-1 w-20 rounded"></div>
            </div>
          </div>
          <div className="relative">
            <div className="text-center">
              <img 
                src="https://blog.ipleaders.in/wp-content/uploads/2017/05/iPleaders-12.jpg" 
                alt="Company Logo"
                className="w-62 h-62 mx-auto object-contain rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-yellow-600 font-bold uppercase tracking-wider text-sm">
              What Drives Us
            </span>
            <h2 className="text-5xl font-bold text-gray-900 mt-4 mb-6">
              Our Core Values
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              These principles guide every decision we make and every service we provide
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 group border-2 border-indigo-100 hover:border-yellow-400">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl mb-6 text-white group-hover:scale-110 transition-transform shadow-lg">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="bg-gradient-to-r from-yellow-400 via-yellow-400 to-orange-200 rounded-3xl p-12 text-center shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          </div>
          <div className="relative">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Ready to Start Your Journey?
            </h2>
            <p className="text-gray-800 text-lg mb-8 max-w-2xl mx-auto">
              Experience the difference that quality service and reliable vehicles make. 
              Book your next adventure with us today.
            </p>
            <button
              onClick={handleClick}
              className="bg-white text-gray-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors shadow-xl hover:shadow-2xl transform hover:scale-105"
            >
              Browse Our Fleet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;